// Pestaña «Analizador»: dibuja las señales que registran los analizadores lógicos del circuito.
import { pinName, escapeHtml } from './panels.js';

const WINDOWS = [
  [100, '0.1 ms'], [500, '0.5 ms'], [1000, '1 ms'], [2000, '2 ms'], [5000, '5 ms'], [10000, '10 ms'], [20000, '20 ms'],
  [50000, '50 ms'], [100000, '100 ms'], [200000, '200 ms'], [500000, '0.5 s'], [1e6, '1 s'], [2e6, '2 s'], [5e6, '5 s'],
];
const KEEP_US = 10e6;
const COLORS = ['#22b8c8', '#f0883e', '#a371f7', '#56d364', '#f85149', '#e3b341', '#79c0ff', '#ff7b72'];

export function setupAnalyzer(app) {
  const canvas = document.getElementById('analyzer');
  const sel = document.getElementById('analyzer-window');
  const pick = document.getElementById('analyzer-part');
  const pauseBtn = document.getElementById('analyzer-pause');
  const empty = document.getElementById('analyzer-empty');
  sel.innerHTML = WINDOWS.map(([v, l]) => `<option value="${v}"${v === 20000 ? ' selected' : ''}>${l}</option>`).join('');
  let windowUs = 20000;
  let paused = false;
  let frozenEnd = 0;
  const data = new Map(); // id → { t, ch: [{ times: [], vals: [] }] }
  let pending = false;

  const isAnalyzer = (id) => {
    const p = app.project && app.project.parts.find((x) => x.id === id);
    return p && p.type === 'logic-analyzer';
  };
  const analyzers = () => (app.project ? app.project.parts.filter((p) => p.type === 'logic-analyzer' && !p._ghost) : []);

  function refreshPicker() {
    const list = analyzers();
    const cur = pick.value;
    pick.innerHTML = list.map((p) => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.id)}</option>`).join('');
    if (list.some((p) => p.id === cur)) pick.value = cur;
    pick.hidden = list.length < 2;
    empty.hidden = list.length > 0;
    canvas.hidden = list.length === 0;
  }

  function append(id, v) {
    let d = data.get(id);
    if (!d) {
      d = { t: v.t, ch: Array.from({ length: 8 }, (_, i) => ({ times: [v.t], vals: [v.values[i]] })), overflow: false };
      data.set(id, d);
      // Los flancos de este cuadro ocurrieron antes de v.t: el valor inicial es el previo al primero.
      for (let i = 0; i < v.edges.length; i += 3) {
        const c = d.ch[v.edges[i + 1]];
        if (c.times.length === 1) {
          c.times[0] = v.edges[i] - 1;
          c.vals[0] = v.edges[i + 2] ? 0 : 1;
        }
      }
    }
    for (let i = 0; i < v.edges.length; i += 3) {
      const c = d.ch[v.edges[i + 1]];
      c.times.push(v.edges[i]);
      c.vals.push(v.edges[i + 2]);
    }
    d.t = v.t;
    if (v.overflow) d.overflow = true;
    // Recorta lo viejo
    for (const c of d.ch) {
      let cut = 0;
      while (cut < c.times.length - 1 && c.times[cut + 1] < d.t - KEEP_US) cut++;
      if (cut > 0) {
        c.times.splice(0, cut);
        c.vals.splice(0, cut);
      }
    }
  }

  function channelName(id, i) {
    const w = app.project.wires.find((x) => (x.a.part === id && x.a.pin === 'CH' + i) || (x.b.part === id && x.b.pin === 'CH' + i));
    if (!w) return '';
    const other = w.a.part === id && w.a.pin === 'CH' + i ? w.b : w.a;
    return pinName(app, other.part + ':' + other.pin);
  }

  function measure(c, t0, t1) {
    // Frecuencia por flancos de subida y ciclo de trabajo en la ventana
    let rises = [];
    let high = 0;
    let idx = upper(c.times, t0) - 1;
    if (idx < 0) idx = 0;
    let v = c.vals[idx];
    let tPrev = t0;
    for (let k = idx + 1; k < c.times.length && c.times[k] <= t1; k++) {
      const t = c.times[k];
      if (v) high += t - tPrev;
      if (c.vals[k] && !v) rises.push(t);
      v = c.vals[k];
      tPrev = t;
    }
    if (v) high += t1 - tPrev;
    const duty = high / (t1 - t0);
    let freq = 0;
    if (rises.length >= 2) freq = ((rises.length - 1) / (rises[rises.length - 1] - rises[0])) * 1e6;
    return { freq, duty };
  }

  function draw() {
    pending = false;
    if (!canvas.offsetParent) return;
    const id = pick.value || (analyzers()[0] && analyzers()[0].id);
    const d = id && data.get(id);
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const g = canvas.getContext('2d');
    g.scale(dpr, dpr);
    g.fillStyle = '#10151b';
    g.fillRect(0, 0, w, h);
    const L = 150;
    // Solo los canales conectados (o todos si aún no hay ninguno)
    let chans = [];
    for (let i = 0; i < 8; i++) if (id && channelName(id, i)) chans.push(i);
    if (!chans.length) chans = [0, 1, 2, 3, 4, 5, 6, 7];
    const rowH = Math.max(26, Math.min(44, (h - 22) / chans.length));
    const tEnd = paused ? frozenEnd : d ? d.t : 0;
    const t0 = tEnd - windowUs;
    const X = (t) => L + ((t - t0) / windowUs) * (w - L - 10);
    g.font = '11px ui-monospace, monospace';
    // Cuadrícula de tiempo
    g.strokeStyle = '#1f2833';
    g.fillStyle = '#6b7889';
    for (let k = 0; k <= 10; k++) {
      const x = L + ((w - L - 10) * k) / 10;
      g.beginPath();
      g.moveTo(x, 4);
      g.lineTo(x, h - 16);
      g.stroke();
    }
    g.fillText(`−${fmtTime(windowUs)}`, L, h - 4);
    g.fillText('ahora', w - 48, h - 4);
    for (const [row, i] of chans.entries()) {
      const y0 = 6 + row * rowH;
      const hi = y0 + 4;
      const lo = y0 + rowH - 6;
      g.fillStyle = COLORS[i];
      const name = id ? channelName(id, i) : '';
      g.fillText(`CH${i}${name ? ' ' + name : ''}`, 6, y0 + rowH / 2 - 2);
      if (!d) continue;
      const c = d.ch[i];
      const m = measure(c, Math.max(t0, c.times[0]), tEnd);
      g.fillStyle = '#6b7889';
      g.fillText(m.freq ? `${fmtFreq(m.freq)} · ${Math.round(m.duty * 100)}%` : m.duty > 0.5 ? 'HIGH' : 'LOW', 6, y0 + rowH / 2 + 10);
      g.strokeStyle = COLORS[i];
      g.lineWidth = 1.4;
      g.beginPath();
      let k = Math.max(0, upper(c.times, t0) - 1);
      let v = c.vals[k];
      g.moveTo(X(Math.max(t0, c.times[k])), v ? hi : lo);
      let lastX = -1;
      for (k = k + 1; k < c.times.length && c.times[k] <= tEnd; k++) {
        const x = X(c.times[k]);
        if (x - lastX < 0.5 && k + 1 < c.times.length && c.times[k + 1] <= tEnd) {
          // Muchas transiciones en el mismo píxel: barra vertical
          g.lineTo(x, hi);
          g.lineTo(x, lo);
          v = c.vals[k];
          continue;
        }
        g.lineTo(x, v ? hi : lo);
        v = c.vals[k];
        g.lineTo(x, v ? hi : lo);
        lastX = x;
      }
      g.lineTo(X(tEnd), v ? hi : lo);
      g.stroke();
    }
    if (d && d.overflow) {
      g.fillStyle = '#e3b341';
      g.fillText('Demasiados flancos: se omitieron algunos', L + 8, h - 4);
    }
  }

  function schedule() {
    if (!pending) {
      pending = true;
      requestAnimationFrame(draw);
    }
  }

  app.on('frame', (f) => {
    if (!f) return;
    let got = false;
    for (const [id, v] of f.visuals) {
      if (v && v.edges && isAnalyzer(id)) {
        append(id, v);
        got = true;
      }
    }
    if (got && !paused) schedule();
  });
  app.on('serial-reset', () => {
    data.clear();
    schedule();
  });
  app.on('layout', () => {
    refreshPicker();
    schedule();
  });
  app.on('project-loaded', () => {
    data.clear();
    refreshPicker();
  });
  sel.addEventListener('change', () => {
    windowUs = Number(sel.value);
    schedule();
  });
  pick.addEventListener('change', schedule);
  pauseBtn.addEventListener('click', () => {
    paused = !paused;
    const id = pick.value || (analyzers()[0] && analyzers()[0].id);
    frozenEnd = id && data.get(id) ? data.get(id).t : 0;
    pauseBtn.textContent = paused ? 'Continuar' : 'Congelar';
    schedule();
  });
  new ResizeObserver(schedule).observe(canvas);
  document.querySelector('.bottom-tabs [data-tab=analyzer]').addEventListener('click', () => {
    refreshPicker();
    schedule();
  });
  refreshPicker();
}

function upper(arr, t) {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function fmtTime(us) {
  return us >= 1e6 ? us / 1e6 + ' s' : us >= 1000 ? us / 1000 + ' ms' : us + ' µs';
}

function fmtFreq(f) {
  return f >= 1e6 ? (f / 1e6).toFixed(2) + ' MHz' : f >= 1000 ? (f / 1000).toFixed(2) + ' kHz' : f.toFixed(1) + ' Hz';
}
